import React, { useEffect, useState } from 'react'
import { useAuthVerifyTwoFactorToken } from '../../api/auth/useAuthVerifyTwoFactorToken'
import { useForm, FormProvider } from 'react-hook-form'
import { Button, TextInput } from '@demandlocal/components'
import { useLocation } from 'react-router'
import { setAuthenticated, setUser } from '../../store/reducers/userReducer'
import { useDispatch } from 'react-redux'
import { Link, Route, Routes, useNavigate } from 'react-router-dom'
import { ReactComponent as Logo } from '../../images/logo_black.svg'
import { ReactComponent as IconLock } from '../../images/icons/icon_lock.svg'
import { GA4 } from '../../lib/utils/ga4'
import mtm from '../../lib/utils/mtm'
import { useDealershipStore } from '../../store/zustand/dealershipStore'
import { match } from 'ts-pattern'
import { useTwoFactorSmsGenerateToken } from '../../api/user/useTwoFactorSmsGenerateToken'
import { useGetUser } from '../../api/auth/useGetUser'

const MultiFactorAuth = () => {
  return (
    <Routes>
      <Route path={'authenticator'} element={<MultiFactorAuthToken next={''} />} />
      <Route path={'sms'} element={<MultiFactorAuthSMS next={''} />} />
    </Routes>
  )
}

const MultiFactorAuthSMS = ({ next }: { next: string }) => {
  const { data: user } = useGetUser()
  const setDealership = useDealershipStore((state) => state.setDealership)
  const { mutateAsync } = useAuthVerifyTwoFactorToken()
  const formContext = useForm({
    defaultValues: {
      token: '',
    },
  })
  const navigate = useNavigate()
  const {
    reset,
    setError,
    handleSubmit,
    formState: { isSubmitting },
  } = formContext
  const dispatch = useDispatch()
  const { mutate: generateSMSToken } = useTwoFactorSmsGenerateToken()

  const onSubmit = async (values: { token: string }) => {
    await mutateAsync(
      {
        token: values.token,
        type: 'sms',
      },
      {
        onSuccess: () => {
          if (user?.terms_accepted_date) {
            dispatch(setUser(user))
            dispatch(setAuthenticated(true))
            const organizations = user?.organizations ?? []
            const dealership = organizations[0]?.dealerships?.[0]
            GA4.configureUser(user)
            GA4.sendEvent({
              event_name: 'login_success',
              event_params: {},
            })
            mtm.sendEvent('Login', 'Success', user.email)
            setDealership(dealership)
            navigate('/dashboard')
          } else {
            navigate('../terms-and-conditions', {
              state: {
                user: user,
                next,
              },
            })
          }
        },
        onError: () => {
          reset()
          setError('token', { message: 'Invalid token' })
        },
      },
    )
  }

  const [firstCodeSent, setSendFirstCode] = useState(false)

  return (
    <FormProvider {...formContext}>
      <div className={'h-screen flex justify-center font-Spartan'}>
        <div className={'w-full max-w-2xl mx-auto my-auto'}>
          <div className={'flex justify-center p-10'}>
            <Logo />
          </div>
          <div className={'w-full p-10 rounded-lg border-2'}>
            <div className={'flex flex-col items-center justify-center gap-4'}>
              <div
                className={
                  'rounded-full bg-primary-persian-blue/5 w-32 h-32 flex items-center justify-center'
                }>
                <IconLock
                  className={
                    'stroke-primary-persian-blue fill-primary-persian-blue w-24 h-16'
                  }
                />
              </div>
              <div
                className={
                  'font-Spartan font-bold flex items-center justify-center text-3xl text-primary-xiketic'
                }>
                Two Factor Authentication
              </div>
              <div className={'text-base text-davy-gray'}>
                {match(firstCodeSent)
                  .with(true, () => 'Please enter the 6 digit code sent to your phone.')
                  .otherwise(
                    () =>
                      'When you have your phone ready, click the button below to send a code to your phone.',
                  )}
              </div>
            </div>
            {firstCodeSent && (
              <form className={'flex flex-col gap-8'} onSubmit={handleSubmit(onSubmit)}>
                <div className={'pt-8 gap-4 flex flex-col'}>
                  <TextInput
                    inverted={true}
                    name={'token'}
                    label={'Authentication code'}
                    autoComplete={'one-time-code'}
                    info={'Enter the 6 digit code sent to your phone.'}
                  />
                </div>

                <div>
                  <Button disabled={isSubmitting} type={'submit'}>
                    Login
                  </Button>
                </div>
              </form>
            )}
            {!firstCodeSent && (
              <div className={'flex items-center justify-center'}>
                <Button
                  type={'button'}
                  onClick={(e) => {
                    e.preventDefault()
                    setSendFirstCode(true)
                    generateSMSToken({
                      phone_number: user?.phone,
                    })
                  }}>
                  Send SMS code
                </Button>
              </div>
            )}
          </div>
          <div className={'border-2 mt-4 p-10 gap-4 flex flex-col rounded-md'}>
            <h3 className={'text-xl font-extrabold'}>Having trouble?</h3>
            <div className={'flex flex-col gap-2 items-start'}>
              <Button
                variant={'tertiary'}
                type={'button'}
                disabled={!firstCodeSent}
                animate={false}
                onClick={(e) => {
                  e.preventDefault()
                  generateSMSToken({
                    phone_number: user?.phone,
                  })
                }}>
                Resend code
              </Button>
              {!!user?.two_factor_enabled && (
                <Button
                  animate={false}
                  variant={'tertiary'}
                  type={'button'}
                  onClick={(e) => {
                    e.preventDefault()
                    navigate('../authenticator')
                  }}>
                  Use Authenticator
                </Button>
              )}
              <p className={'text-sm text-primary-xiketic font-medium'}>
                Contact support if you're having trouble with your code.
              </p>
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  )
}
const MultiFactorAuthToken = ({ next }: { next: string }) => {
  const setDealership = useDealershipStore((state) => state.setDealership)
  const { mutateAsync } = useAuthVerifyTwoFactorToken()
  const formContext = useForm({
    defaultValues: {
      token: '',
    },
  })
  // get user from navigation state
  const { data: user } = useGetUser()
  const navigate = useNavigate()
  const {
    reset,
    setError,
    handleSubmit,
    formState: { isSubmitting },
  } = formContext
  const dispatch = useDispatch()
  const { mutate: generateSMSToken } = useTwoFactorSmsGenerateToken()

  const onSubmit = async (values: { token: string }) => {
    await mutateAsync(
      {
        token: values.token,
        type: 'authenticator',
      },
      {
        onSuccess: () => {
          if (user?.terms_accepted_date) {
            dispatch(setUser(user))
            dispatch(setAuthenticated(true))
            const organizations = user?.organizations ?? []
            const dealership = organizations[0]?.dealerships?.[0]
            GA4.configureUser(user)
            GA4.sendEvent({
              event_name: 'login_success',
              event_params: {},
            })
            mtm.sendEvent('Login', 'Success', user.email)
            setDealership(dealership)
            navigate('/dashboard')
          } else {
            navigate('../terms-and-conditions', {
              state: {
                user: user,
                next,
              },
            })
          }
        },
        onError: (error) => {
          console.log(error)
          reset()
          setError('token', { message: 'Invalid token' })
        },
      },
    )
  }
  /*
  useEffect(() => {
    generateSMSToken({ phone_number: '+40728391902' })
  }, [data.phone, generateSMSToken])*/

  return (
    <FormProvider {...formContext}>
      <div className={'h-screen flex justify-center font-Spartan'}>
        <div className={'w-full max-w-2xl mx-auto my-auto'}>
          <div className={'flex justify-center p-10'}>
            <Logo />
          </div>
          <div className={'w-full p-10 rounded-lg border-2'}>
            <div className={'flex flex-col items-center justify-center gap-4'}>
              <div
                className={
                  'rounded-full bg-primary-persian-blue/5 w-32 h-32 flex items-center justify-center'
                }>
                <IconLock
                  className={
                    'stroke-primary-persian-blue fill-primary-persian-blue w-24 h-16'
                  }
                />
              </div>
              <div
                className={
                  'font-Spartan font-bold flex items-center justify-center text-3xl text-primary-xiketic'
                }>
                Two Factor Authentication
              </div>
              <div className={'text-base text-davy-gray'}>
                Please enter the 6 digit code from your authenticator app to continue.
              </div>
            </div>
            <form className={'flex flex-col gap-8'} onSubmit={handleSubmit(onSubmit)}>
              <div className={'pt-8 gap-4 flex flex-col'}>
                <TextInput
                  inverted={true}
                  name={'token'}
                  label={'Authentication code'}
                  autoComplete={'one-time-code'}
                  info={
                    'Open the two-factor authenticator (TOTP) app on your mobile device to view your authentication code.'
                  }
                />
              </div>

              <div>
                <Button disabled={isSubmitting} type={'submit'}>
                  Login
                </Button>
              </div>
            </form>
          </div>
          <div className={'border-2 mt-4 p-10 gap-4 flex flex-col rounded-md'}>
            <h3 className={'text-xl font-extrabold'}>Having trouble?</h3>
            <div className={'flex flex-col gap-2 items-start'}>
              {!!user?.sms_two_factor_enabled && (
                <Button
                  animate={false}
                  variant={'tertiary'}
                  type={'button'}
                  onClick={(e) => {
                    e.preventDefault()
                    navigate('../sms')
                  }}>
                  Use SMS
                </Button>
              )}
              <p className={'text-sm text-primary-xiketic font-medium'}>
                Contact support if you're having trouble with your code.
              </p>
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  )
}

export default MultiFactorAuth
